/**
 * QcCharts — the "Charts" view of the Ball-by-Ball QC page.
 *
 * Drawn straight from the QC page's own ball entries
 * (/api/ballbyball/match-ball-entries/), so they show exactly what has
 * been coded — including a match still in progress — and follow every
 * filter the QC table has applied (innings, striker, bowler, …).
 *
 * The look follows the analyzer (zencricketanalyzer.com), so a coder
 * sees the same pictures here that the customer will see there:
 *
 *   · Summary tiles — runs, wickets, overs, run rate, dot %, boundary %
 *   1. Wagon wheel — the analyzer's green field; Lines | Sectors
 *   2. Pitch map   — the broadcast length picture from the bowler's end,
 *                    with a Grid view of length × line
 *   3. Beehive     — where the ball reached the batter, stumps drawn in,
 *                    with a Grid view of height × line
 *   4. Manhattan   — runs per over, phases shaded, run-rate line
 *   5. Worm        — cumulative runs, phases shaded, wickets marked
 *   6. Scoring mix — donut of outcomes
 *   7. Shot types  — per stroke, balls split by outcome
 *
 * Colours. Run outcomes use the analyzer's palette (dot slate, 1–3 sky,
 * four yellow, six orange). The analyzer's wicket rose sits too close to
 * six orange to tell apart (ΔE 10.7, validated), so a wicket here is a
 * white ✕ marker — a shape, not a fifth hue. The two batting sides keep
 * their own validated pair. Every chart also prints its numbers, so
 * nothing is read from colour alone.
 *
 * Coded zones, not measured positions: the entries carry the coded
 * length / line / height, so the pitch map and beehive place each ball
 * inside its coded cell (spread a little so a cluster reads as one), the
 * way the analyzer draws balls it only has coded lengths for.
 */
import { useContext, useMemo, useState } from "react";
import {
  BAND_BY_KEY, BOUNDARY_R, QcFormatContext, CHART_CSS, FC, FIELD_HALO, GRID_LENGTHS, HEIGHTS, LENGTH_BANDS, LINES, LINE_IDX, LINE_LABEL, MARK_EDGE, NOT_PITCHED, OC, OUTCOME_LEGEND, RING_R, SECTORS, STRIKER_Y, ballText, formatOvers, jitter, outcomeColour, outcomeKind, pct, pct0, phasesFor, placeShots, polar, slice, toBall, unit, useInnings,
} from "./qcData";
import { Card, Empty, Field, Legend, PhaseBands, SvgX, Swatch, Toggle } from "./qcUi";
import { useTip } from "./useTip";
import { Tabs } from "./qcUi";
import { consistencyFlags } from "./qcChecks";
import MatchCharts from "./MatchCharts";
import BattingCharts from "./BattingCharts";
import BowlingCharts from "./BowlingCharts";
import FieldingCharts from "./FieldingCharts";
import CodingChecks from "./CodingChecks";


// ── Summary tiles ────────────────────────────────────────────────────
function SummaryTiles({ balls }) {
  const s = useMemo(() => {
    let runs = 0, wkts = 0, legal = 0, dots = 0, faced = 0, fours = 0, sixes = 0, extras = 0;
    for (const b of balls) {
      runs += b.total;
      extras += b.extras;
      if (b.wicket) wkts += 1;
      if (b.legal) {
        legal += 1;
        if (b.total === 0) dots += 1;
      }
      if (b.faced) faced += 1;
      if (b.offBat === 4) fours += 1;
      if (b.offBat === 6) sixes += 1;
    }
    return { runs, wkts, legal, dots, faced, fours, sixes, extras };
  }, [balls]);
  const tiles = [
    { label: "Runs", value: s.runs, sub: `${s.wkts} wkt${s.wkts === 1 ? "" : "s"}` },
    { label: "Overs", value: `${Math.floor(s.legal / 6)}.${s.legal % 6}`, sub: `${s.legal} legal balls` },
    { label: "Run rate", value: s.legal ? ((s.runs * 6) / s.legal).toFixed(2) : "—", sub: "per over" },
    { label: "Dot balls", value: pct0(s.dots, s.legal), sub: `${s.dots} of ${s.legal}` },
    { label: "Boundaries", value: s.fours + s.sixes, sub: `${s.fours} × 4 · ${s.sixes} × 6` },
    { label: "Boundary %", value: pct0(s.fours + s.sixes, s.faced), sub: "of balls faced" },
    { label: "Extras", value: s.extras, sub: "wd · nb · b · lb · pen" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-lg border border-border-default bg-bg-secondary px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-text-secondary">{t.label}</div>
          <div className="text-lg font-semibold text-text-primary tabular-nums leading-tight">{t.value}</div>
          <div className="text-[10px] text-text-secondary tabular-nums truncate">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

const WAGON_KINDS = [
  { key: "0", label: "Dots" }, { key: "13", label: "1–3" }, { key: "4", label: "4s" },
  { key: "6", label: "6s" }, { key: "w", label: "Wkts" },
];

function WagonWheel({ balls }) {
  const [view, setView] = useState("lines");
  // null = follow the selection: a left-hander's view when every pinned
  // ball in view was faced by a left-hander (QC filtered to one LHB).
  const [hand, setHand] = useState(null);
  const [kinds, setKinds] = useState(() => new Set(["13", "4", "6", "w"]));
  const tip = useTip();
  const placed = useMemo(() => placeShots(balls), [balls]);

  const stats = useMemo(() => {
    const rows = SECTORS.map((s) => ({ ...s, runs: 0, balls: 0, fours: 0, sixes: 0, wkts: 0 }));
    for (const p of placed) {
      const r = rows[p.sector];
      r.balls += 1;
      r.runs += p.b.offBat;
      if (p.b.offBat === 4) r.fours += 1;
      if (p.b.offBat === 6) r.sixes += 1;
      if (p.b.wicket) r.wkts += 1;
    }
    const total = rows.reduce((t, r) => t + r.runs, 0);
    const max = Math.max(0, ...rows.map((r) => r.runs));
    return { rows, total, max };
  }, [placed]);

  const kindCounts = useMemo(() => {
    const c = {};
    for (const p of placed) { const k = outcomeKind(p.b); c[k] = (c[k] || 0) + 1; }
    return c;
  }, [placed]);

  if (!placed.length) {
    return <Card title="Wagon wheel"><Empty>No shots with a wagon-wheel pin in this selection.</Empty></Card>;
  }

  // Shots are placed in a right-hander's frame; the left-hander's view is
  // that picture mirrored across the straight line — points, wedges and
  // labels together. Sector names are about the stroke (a left-hander's
  // cover drive is still "Cover"), so the table and totals do not change.
  const allLefty = placed.every((p) => p.b.lefty);
  const lh = (hand ?? (allLefty ? "L" : "R")) === "L";
  const px = (x) => (lh ? 2 * FC - x : x);
  const span = (sct) => (lh ? [-(sct.start + 45), -sct.start] : [sct.start, sct.start + 45]);
  const mid = (sct) => (lh ? -(sct.start + 22.5) : sct.start + 22.5);
  const mixed = lh ? placed.length - placed.filter((p) => p.b.lefty).length : placed.filter((p) => p.b.lefty).length;

  const shown = placed
    .filter((p) => kinds.has(outcomeKind(p.b)))
    .sort((a, c) => (a.b.wicket ? 1 : 0) - (c.b.wicket ? 1 : 0) || a.b.offBat - c.b.offBat);
  const toggleKind = (k) => setKinds((prev) => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  return (
    <Card
      wide
      title="Wagon wheel"
      subtitle={`${stats.total} runs off the bat from ${placed.length} pinned balls · ${lh ? "left" : "right"}-hander's view${mixed ? ` (${mixed} ${lh ? "right" : "left"}-hander ball${mixed === 1 ? "" : "s"} mirrored)` : ""}`}
      controls={<>
        <Toggle label="Batter view" value={lh ? "L" : "R"} onChange={setHand}
          options={[{ key: "R", label: "RHB" }, { key: "L", label: "LHB" }]} />
        <Toggle label="Wagon wheel view" value={view} onChange={setView}
          options={[{ key: "lines", label: "Lines" }, { key: "sectors", label: "Sectors" }]} />
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
        <div className="lg:col-span-3">
          {view === "lines" && (
            <div className="flex flex-wrap justify-center gap-1 mb-2">
              {WAGON_KINDS.map((k) => {
                const on = kinds.has(k.key);
                const item = OUTCOME_LEGEND.find((o) => o.key === k.key);
                return (
                  <button key={k.key} type="button" onClick={() => toggleKind(k.key)} aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] transition ${on
                      ? "border-emerald-500/60 bg-emerald-600/15 text-text-primary"
                      : "border-border-default text-text-secondary opacity-60 hover:opacity-100"}`}>
                    <Swatch item={item} />
                    {k.label}
                    <span className="tabular-nums text-text-secondary">{kindCounts[k.key] || 0}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div data-tip-root className="relative max-w-[440px] mx-auto">
            <svg viewBox="0 0 100 100" className="w-full h-auto block" role="img" aria-label="Wagon wheel">
              <Field>
                {view === "sectors" && stats.rows.map((r, i) => (
                  <path key={`h${i}`} d={slice(...span(r), BOUNDARY_R)}
                    fill="#f59e0b" fillOpacity={r.runs ? 0.12 + 0.5 * (r.runs / (stats.max || 1)) : 0}
                    stroke="rgba(255,255,255,0.35)" strokeWidth="0.2"
                    onMouseMove={(ev) => tip.show(ev, [r.name, `${r.runs} runs · ${pct(r.runs, stats.total)} of runs`, `${r.balls} balls · ${r.fours} × 4 · ${r.sixes} × 6${r.wkts ? ` · ${r.wkts} wkt` : ""}`])}
                    onMouseLeave={tip.hide} />
                ))}
              </Field>

              {view === "lines" && SECTORS.map((s, i) => {
                const [bx, by] = polar(mid(s), (RING_R + BOUNDARY_R) / 2);
                const [ix, iy] = polar(mid(s), RING_R * 0.6);
                return (
                  <g key={`l${i}`} pointerEvents="none">
                    <text x={bx} y={by + 1} textAnchor="middle" fontSize="2.6" fill="#e7f5ec" fontWeight="600" style={FIELD_HALO}>{s.short}</text>
                    <text x={ix} y={iy + 1} textAnchor="middle" fontSize="2" fill="#e7f5ec" opacity="0.85" style={FIELD_HALO}>{s.inner}</text>
                  </g>
                );
              })}

              {view === "lines" && shown.map((p) => (
                <g key={p.b.i}>
                  <line x1={FC} y1={STRIKER_Y} x2={px(p.x)} y2={p.y} stroke={outcomeColour(p.b)}
                    strokeOpacity={p.b.offBat >= 4 ? 0.95 : 0.8} strokeWidth={p.b.offBat >= 4 ? 0.6 : 0.45}
                    strokeLinecap="round" pointerEvents="none" />
                  {p.b.wicket
                    ? <SvgX x={px(p.x)} y={p.y} s={1.5} />
                    : <circle cx={px(p.x)} cy={p.y} r="0.95" fill={outcomeColour(p.b)} stroke={MARK_EDGE} strokeWidth="0.18" pointerEvents="none" />}
                  <circle cx={px(p.x)} cy={p.y} r="2.4" fill="transparent"
                    onMouseMove={(ev) => tip.show(ev, ballText(p.b))} onMouseLeave={tip.hide} />
                </g>
              ))}

              {view === "sectors" && stats.rows.map((r, i) => {
                const [ax, ay] = polar(mid(r), 30);
                const [nx, ny] = polar(mid(r), 42.5);
                const h = r.wkts ? 9.8 : 7.2, w = 15;
                return (
                  <g key={`s${i}`} pointerEvents="none">
                    <text x={nx} y={ny + 0.8} textAnchor="middle" fontSize="2.3" fill="#e7f5ec" opacity="0.9">{r.short}</text>
                    <rect x={ax - w / 2} y={ay - h / 2} width={w} height={h} rx="1" fill="rgba(15,23,42,0.78)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.15" />
                    <text x={ax} y={ay - h / 2 + 2.4} textAnchor="middle" dominantBaseline="middle" fontSize="3.4" fontWeight="700"
                      fill={r.runs ? "#fde68a" : "#94a3b8"} style={{ fontVariantNumeric: "tabular-nums" }}>{pct0(r.runs, stats.total)}</text>
                    <text x={ax} y={ay - h / 2 + 5.3} textAnchor="middle" dominantBaseline="middle" fontSize="2.3" fill="#e2e8f0"
                      style={{ fontVariantNumeric: "tabular-nums" }}>{`${r.runs}r · ${r.balls}b`}</text>
                    {r.wkts > 0 && (
                      <text x={ax} y={ay - h / 2 + 7.9} textAnchor="middle" dominantBaseline="middle" fontSize="2.3" fill="#fff">{`${r.wkts} ✕`}</text>
                    )}
                  </g>
                );
              })}
              <text x="3" y="5" fontSize="2.8" className="fill-text-secondary" pointerEvents="none">{lh ? "LEG" : "OFF"}</text>
              <text x="97" y="5" fontSize="2.8" textAnchor="end" className="fill-text-secondary" pointerEvents="none">{lh ? "OFF" : "LEG"}</text>
            </svg>
            {tip.node}
          </div>
          {view === "lines" && <div className="mt-2 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>}
        </div>

        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Runs by sector</div>
          <table className="w-full text-[11px]">
            <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
              <tr>
                <th className="text-left py-1 font-normal">Sector</th>
                <th className="text-left font-normal w-[28%]" />
                <th className="text-right font-normal">Runs</th>
                <th className="text-right font-normal">Balls</th>
                <th className="text-right font-normal">4s</th>
                <th className="text-right font-normal">6s</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {stats.rows.map((r) => (
                <tr key={r.name} className="border-t border-border-default">
                  <td className="py-1.5 pr-2 text-text-primary whitespace-nowrap">{r.short}</td>
                  <td className="pr-2">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 rounded-r-[3px] bg-amber-500/80" style={{ width: `${stats.max ? (r.runs / stats.max) * 100 : 0}%`, minWidth: r.runs ? 2 : 0 }} />
                      <span className="text-text-secondary text-[10px]">{pct0(r.runs, stats.total)}</span>
                    </div>
                  </td>
                  <td className="text-right text-text-primary font-semibold">{r.runs}</td>
                  <td className="text-right text-text-secondary">{r.balls}</td>
                  <td className="text-right text-text-secondary">{r.fours || ""}</td>
                  <td className="text-right text-text-secondary">{r.sixes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            {(() => {
              const off = [5, 6, 7, 0].reduce((t, i) => t + stats.rows[i].runs, 0);
              const leg = stats.total - off;
              return [["Off side", off], ["Leg side", leg]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-border-default px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-text-secondary">{k}</div>
                  <div className="text-text-primary font-semibold tabular-nums">{v} <span className="text-text-secondary font-normal">· {pct0(v, stats.total)}</span></div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── 2. Pitch map ─────────────────────────────────────────────────────
function PitchView({ balls }) {
  const tip = useTip();
  const d = useMemo(() => {
    const pts = [];
    const bands = {};
    let notPitched = 0, noLength = 0, noLine = 0;
    for (const b of balls) {
      if (!b.length) { noLength += 1; continue; }
      if (NOT_PITCHED.has(b.length)) { notPitched += 1; continue; }
      const band = BAND_BY_KEY[b.length];
      if (!band) continue;
      bands[band.key] = (bands[band.key] || 0) + 1;
      if (LINE_IDX[b.line] == null) noLine += 1;
      pts.push({ b, band });
    }
    pts.sort((a, c) => (a.b.wicket ? 1 : 0) - (c.b.wicket ? 1 : 0));
    return { pts, bands, notPitched, noLength, noLine, pitched: pts.length };
  }, [balls]);

  const W = 100, X0 = 23, X1 = 69, TOP = 9, MY = 7.6;
  const yOf = (m) => TOP + m * MY;
  const BOTTOM = yOf(12);
  const H = BOTTOM + 8;
  const BOX_X = 71;
  const strip = (X1 - X0) / LINES.length;

  if (!d.pitched) return <Empty>No balls with a pitched length coded in this selection.</Empty>;

  return (
    <div>
      <div data-tip-root className="relative max-w-[460px] mx-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="Where the balls pitched">
          <rect x="0" y="0" width={W} height={H} rx="2" fill="#14532d" />
          {LENGTH_BANDS.map((bd) => (
            <rect key={bd.key} x={X0} y={yOf(bd.from)} width={X1 - X0} height={yOf(bd.to) - yOf(bd.from)} fill={bd.fill} fillOpacity="0.22" />
          ))}
          {LINES.slice(1).map((l, i) => (
            <line key={l.key} x1={X0 + strip * (i + 1)} y1={TOP} x2={X0 + strip * (i + 1)} y2={BOTTOM}
              stroke="#fff" strokeOpacity="0.07" strokeWidth="0.2" />
          ))}
          <rect x={X0} y={TOP - 4} width={X1 - X0} height={BOTTOM - TOP + 4} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.3" />
          <line x1={X0} y1={TOP} x2={X1} y2={TOP} stroke="#fff" strokeOpacity="0.7" strokeWidth="0.35" />
          {[-1, 0, 1].map((k) => (
            <rect key={k} x={(X0 + X1) / 2 + k * 1.1 - 0.35} y={TOP - 5} width="0.7" height="5" fill="#facc15" />
          ))}
          {LENGTH_BANDS.map((bd) => (
            <g key={`m${bd.key}`}>
              <line x1={X0 - 1.5} y1={yOf(bd.from)} x2={X1} y2={yOf(bd.from)} stroke="#fff" strokeOpacity={bd.from ? 0.18 : 0} strokeWidth="0.25" strokeDasharray="1 1" />
              <text x={X0 - 2} y={(yOf(bd.from) + yOf(bd.to)) / 2 + 1} textAnchor="end" fontSize="2.4" fill="#e2e8f0" fontWeight="600">{bd.label}</text>
            </g>
          ))}
          {LINES.map((l, i) => (
            <text key={`c${l.key}`} x={X0 + strip * (i + 0.5)} y={BOTTOM + 3.6} textAnchor="middle" fontSize="2.1" fill="#e2e8f0" opacity="0.85">{l.short}</text>
          ))}
          <text x={X0} y={H - 1} fontSize="2" fill="#e2e8f0" opacity="0.6">OFF</text>
          <text x={X1} y={H - 1} fontSize="2" fill="#e2e8f0" opacity="0.6" textAnchor="end">LEG</text>

          {d.pts.map(({ b, band }) => {
            const li = LINE_IDX[b.line] ?? 3;
            const cx = X0 + strip * (li + 0.5) + jitter(b.i * 2 + 1, strip * 0.36);
            const cy = yOf(band.from + (band.to - band.from) * (0.15 + 0.7 * unit(b.i * 2 + 2)));
            return (
              <g key={b.i}>
                {b.wicket
                  ? <SvgX x={cx} y={cy} s={1.35} />
                  : <circle cx={cx} cy={cy} r="1" fill={outcomeColour(b)} fillOpacity="0.95" stroke={MARK_EDGE} strokeWidth="0.18" pointerEvents="none" />}
                <circle cx={cx} cy={cy} r="2" fill="transparent"
                  onMouseMove={(ev) => tip.show(ev, [...ballText(b), `${BAND_BY_KEY[b.length].label} · ${LINE_LABEL[b.line] || "line not coded"}`])}
                  onMouseLeave={tip.hide} />
              </g>
            );
          })}

          {LENGTH_BANDS.map((bd) => {
            const y0 = yOf(bd.from), y1 = yOf(bd.to), mid = (y0 + y1) / 2;
            const n = d.bands[bd.key] || 0;
            const bh = Math.min(10, Math.max(6.6, y1 - y0 - 1));
            return (
              <g key={`b${bd.key}`} opacity={n ? 1 : 0.45}>
                <rect x={BOX_X} y={mid - bh / 2} width={W - BOX_X - 1.5} height={bh} rx="1" fill={bd.fill} fillOpacity="0.92" />
                <text x={BOX_X + 1.8} y={mid - bh / 2 + 2.9} fontSize="2.2" fill="#0f172a" fontWeight="700">{bd.label}</text>
                <text x={W - 3} y={mid + bh / 2 - 1.3} textAnchor="end" fontSize="3.8" fill="#0f172a" fontWeight="800"
                  style={{ fontVariantNumeric: "tabular-nums" }}>{pct0(n, d.pitched)}</text>
                <text x={BOX_X + 1.8} y={mid + bh / 2 - 1.3} fontSize="2.1" fill="#0f172a" style={{ fontVariantNumeric: "tabular-nums" }}>{n}</text>
              </g>
            );
          })}
        </svg>
        {tip.node}
      </div>
      <div className="mt-2 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>
      <p className="mt-1.5 text-[10px] text-text-secondary text-center tabular-nums">
        {d.pitched} balls placed by coded length
        {d.notPitched > 0 && <> · {d.notPitched} full toss / beamer (did not pitch, not in the %)</>}
        {d.noLength > 0 && <> · {d.noLength} with no length</>}
        {d.noLine > 0 && <> · {d.noLine} with no line (drawn on middle)</>}
      </p>
    </div>
  );
}

// ── 3. Beehive ───────────────────────────────────────────────────────
function BeehiveView({ balls }) {
  const tip = useTip();
  const d = useMemo(() => {
    const pts = [];
    const rows = {};
    for (const b of balls) {
      if (!b.height || !b.lineAtBat) continue;
      const ri = HEIGHTS.indexOf(b.height);
      const ci = LINE_IDX[b.lineAtBat];
      if (ri < 0 || ci == null) continue;
      rows[b.height] = (rows[b.height] || 0) + 1;
      pts.push({ b, ri, ci });
    }
    pts.sort((a, c) => (a.b.wicket ? 1 : 0) - (c.b.wicket ? 1 : 0));
    return { pts, rows, n: pts.length };
  }, [balls]);

  if (!d.n) return <Empty>No balls with both height and line at the batter coded in this selection.</Empty>;

  const X0 = 17, X1 = 83, Y0 = 5, RH = 10.5, Y1 = Y0 + RH * HEIGHTS.length;
  const CW = (X1 - X0) / LINES.length;
  const H = Y1 + 8;
  const stumpTop = Y0 + RH * 4;
  const maxRow = Math.max(1, ...Object.values(d.rows));

  return (
    <div>
      <div data-tip-root className="relative max-w-[460px] mx-auto">
        <svg viewBox={`0 0 100 ${H}`} className="w-full h-auto block" role="img" aria-label="Beehive">
          <rect x="0" y="0" width="100" height={H} rx="2" fill="#0b1a2e" />
          {HEIGHTS.map((h, i) => (
            <g key={h}>
              <rect x={X0} y={Y0 + RH * i} width={X1 - X0} height={RH} fill={i % 2 ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.065)"} />
              <text x={X0 - 1.5} y={Y0 + RH * (i + 0.5) + 0.9} textAnchor="end" fontSize="2.6" fill="#e2e8f0" fontWeight="600">{h}</text>
            </g>
          ))}
          {LINES.slice(1).map((l, i) => (
            <line key={l.key} x1={X0 + CW * (i + 1)} y1={Y0} x2={X0 + CW * (i + 1)} y2={Y1} stroke="#fff" strokeOpacity="0.07" strokeWidth="0.2" />
          ))}
          {[2, 3, 4].map((ci) => (
            <rect key={ci} x={X0 + CW * (ci + 0.5) - 0.45} y={stumpTop} width="0.9" height={Y1 - stumpTop} fill="#facc15" fillOpacity="0.45" />
          ))}
          <rect x={X0 + CW * 2.5 - 0.6} y={stumpTop - 0.7} width={CW * 2 + 1.2} height="0.7" rx="0.3" fill="#facc15" fillOpacity="0.55" />
          <line x1={X0 - 1} y1={Y1} x2={X1 + 1} y2={Y1} stroke="#fff" strokeOpacity="0.55" strokeWidth="0.35" />
          {LINES.map((l, i) => (
            <text key={`c${l.key}`} x={X0 + CW * (i + 0.5)} y={Y1 + 3.6} textAnchor="middle" fontSize="2.1" fill="#e2e8f0" opacity="0.85">{l.short}</text>
          ))}
          <text x={X0} y={H - 1} fontSize="2" fill="#e2e8f0" opacity="0.6">OFF</text>
          <text x={X1} y={H - 1} fontSize="2" fill="#e2e8f0" opacity="0.6" textAnchor="end">LEG</text>

          {d.pts.map(({ b, ri, ci }) => {
            const cx = X0 + CW * (ci + 0.5) + jitter(b.i * 3 + 1, CW * 0.36);
            const cy = Y0 + RH * (ri + 0.5) + jitter(b.i * 3 + 2, RH * 0.34);
            return (
              <g key={b.i}>
                {b.wicket
                  ? <SvgX x={cx} y={cy} s={1.35} />
                  : <circle cx={cx} cy={cy} r="1" fill={outcomeColour(b)} fillOpacity="0.95" stroke={MARK_EDGE} strokeWidth="0.18" pointerEvents="none" />}
                <circle cx={cx} cy={cy} r="2" fill="transparent"
                  onMouseMove={(ev) => tip.show(ev, [...ballText(b), `${b.height} · ${LINE_LABEL[b.lineAtBat]}`])}
                  onMouseLeave={tip.hide} />
              </g>
            );
          })}

          {HEIGHTS.map((h, i) => {
            const n = d.rows[h] || 0;
            const y = Y0 + RH * i;
            return (
              <g key={`r${h}`} opacity={n ? 1 : 0.4}>
                <rect x={X1 + 2} y={y + 1.6} width={(97 - X1 - 2) * (n / maxRow)} height={RH - 3.2} rx="0.8" fill="#38bdf8" fillOpacity="0.35" />
                <text x={X1 + 3} y={y + RH / 2 + 1} fontSize="2.7" fill="#e2e8f0" fontWeight="700" style={{ fontVariantNumeric: "tabular-nums" }}>{pct0(n, d.n)}</text>
              </g>
            );
          })}
        </svg>
        {tip.node}
      </div>
      <div className="mt-2 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>
      <p className="mt-1.5 text-[10px] text-text-secondary text-center">{d.n} balls · seen from the bowler's end, right-hander's off side on the left</p>
    </div>
  );
}

// ── Grid view shared by the pitch map and beehive ────────────────────
function HeatGrid({ balls, rowKey, rows, colKey }) {
  const [metric, setMetric] = useState("balls");
  const tip = useTip();
  const { cells, max, used, seenRows } = useMemo(() => {
    const cells = {};
    let used = 0;
    const seenRows = new Set();
    for (const b of balls) {
      const r = b[rowKey];
      const c = b[colKey];
      if (!r || !c) continue;
      used += 1;
      seenRows.add(r);
      const k = `${r}|${c}`;
      const cell = cells[k] || (cells[k] = { balls: 0, runs: 0, wickets: 0, dots: 0 });
      cell.balls += 1;
      cell.runs += b.total;
      if (b.wicket) cell.wickets += 1;
      if (b.legal && b.total === 0) cell.dots += 1;
    }
    const max = Math.max(1, ...Object.values(cells).map((c) => c[metric]));
    return { cells, max, used, seenRows };
  }, [balls, rowKey, colKey, metric]);

  // Canonical order, plus any value the list doesn't know, so no ball
  // silently disappears.
  const rowList = [...rows.filter((r) => seenRows.has(r)), ...[...seenRows].filter((r) => !rows.includes(r))];
  if (!used) return <Empty>No balls with both fields coded in this selection.</Empty>;

  return (
    <div data-tip-root className="relative">
      <div className="flex justify-end mb-2">
        <Toggle label="Grid metric" value={metric} onChange={setMetric}
          options={[{ key: "balls", label: "Balls" }, { key: "runs", label: "Runs" }, { key: "wickets", label: "Wkts" }, { key: "dots", label: "Dots" }]} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] border-separate" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th />
              {LINES.map((l) => (
                <th key={l.key} className="text-[9px] uppercase tracking-wider text-text-secondary font-normal px-1 pb-1">{l.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowList.map((r) => (
              <tr key={r}>
                <th className="text-left text-text-secondary font-normal pr-2 whitespace-nowrap">{BAND_BY_KEY[r]?.label || r}</th>
                {LINES.map((l) => {
                  const c = cells[`${r}|${l.key}`];
                  const v = c ? c[metric] : 0;
                  const a = v ? 0.14 + 0.72 * (v / max) : 0;
                  return (
                    <td key={l.key}
                      className={`h-8 min-w-[44px] text-center tabular-nums rounded-[4px] ${a > 0.55 ? "text-white font-semibold" : "text-text-primary"}`}
                      style={{ background: v ? `rgba(var(--qc-heat), ${a})` : "var(--qc-grid)" }}
                      onMouseMove={(ev) => c && tip.show(ev, [
                        `${BAND_BY_KEY[r]?.label || r} · ${l.label}`,
                        `${c.balls} balls · ${c.runs} runs · ${c.wickets} wkts`,
                        `Dots ${c.dots} · Econ ${c.balls ? ((c.runs * 6) / c.balls).toFixed(2) : "—"}`,
                      ])}
                      onMouseLeave={tip.hide}
                    >{v || ""}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-text-secondary mt-2">Columns run off side → leg side for the batter on strike · {used} balls.</p>
      {tip.node}
    </div>
  );
}

function PitchMap({ balls }) {
  const [view, setView] = useState("pitch");
  return (
    <Card title="Pitch map" subtitle="Where the ball pitched — length × line, from the bowler's end"
      controls={<Toggle label="Pitch map view" value={view} onChange={setView}
        options={[{ key: "pitch", label: "Pitch" }, { key: "grid", label: "Grid" }]} />}>
      {view === "pitch"
        ? <PitchView balls={balls} />
        : <HeatGrid balls={balls} rowKey="length" rows={GRID_LENGTHS} colKey="line" />}
    </Card>
  );
}

function Beehive({ balls }) {
  const [view, setView] = useState("hive");
  return (
    <Card title="Beehive" subtitle="Where the ball reached the batter — height × line"
      controls={<Toggle label="Beehive view" value={view} onChange={setView}
        options={[{ key: "hive", label: "Beehive" }, { key: "grid", label: "Grid" }]} />}>
      {view === "hive"
        ? <BeehiveView balls={balls} />
        : <HeatGrid balls={balls} rowKey="height" rows={HEIGHTS} colKey="lineAtBat" />}
    </Card>
  );
}

// ── 4. Manhattan ─────────────────────────────────────────────────────
function Manhattan({ balls }) {
  const fmt = useContext(QcFormatContext);
  const innings = useInnings(balls);
  if (!innings.length) return <Card title="Manhattan"><Empty>No balls in this selection.</Empty></Card>;
  const maxOver = formatOvers(Math.max(...innings.flatMap((i) => i.rows.map((r) => r.over))), fmt);
  const maxRuns = Math.max(8, ...innings.flatMap((i) => i.rows.map((r) => r.runs)));
  return (
    <Card title="Manhattan" subtitle="Runs per over, one panel per innings on the same scale · dashed line = run rate · ✕ = wickets in the over">
      <div className="space-y-4">
        {innings.map((inn) => <ManhattanPanel key={inn.innings} inn={inn} maxOver={maxOver} maxRuns={maxRuns} />)}
      </div>
    </Card>
  );
}

function ManhattanPanel({ inn, maxOver, maxRuns }) {
  const tip = useTip();
  const W = 360, H = 138, padL = 22, padR = 6, padB = 16, padT = 16;
  const plotW = W - padL - padR;
  const bw = plotW / maxOver;
  const x = (o) => padL + o * bw;
  const y = (v) => padT + (H - padT - padB) * (1 - v / maxRuns);
  const step = maxRuns > 24 ? 10 : 5;
  const ticks = [];
  for (let t = 0; t <= maxRuns; t += step) ticks.push(t);
  const best = inn.rows.reduce((m, r) => (r.runs > (m?.runs ?? -1) ? r : m), null);
  return (
    <div data-tip-root className="relative">
      <div className="flex items-center gap-2 text-[11px] mb-1">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: inn.color }} />
        <span className="text-text-secondary">Innings {inn.innings} · {inn.team}</span>
        <span className="text-text-primary font-semibold tabular-nums">{inn.runs}/{inn.wkts}</span>
        <span className="text-text-secondary tabular-nums">({inn.oversText} ov · RR {inn.rr.toFixed(2)})</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label={`Runs per over, innings ${inn.innings}`}>
        <PhaseBands phases={phasesFor(maxOver, fmt)} x={x} y0={padT - 10} y1={H - padB} width={W - padR} />
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--qc-grid)" strokeWidth="0.6" />
            <text x={padL - 4} y={y(t)} fontSize="7" textAnchor="end" dominantBaseline="central" className="fill-text-secondary">{t}</text>
          </g>
        ))}
        {inn.rows.map((r) => {
          const bx = x(r.over - 1) + 1;
          const w = Math.max(1, bw - 2);
          const top = y(r.runs);
          const h = Math.max(0, H - padB - top);
          const rad = Math.min(3, w / 2, h);
          return (
            <g key={r.over}>
              <rect x={x(r.over - 1)} y={padT - 10} width={bw} height={H - padB - padT + 10} fill="transparent"
                onMouseMove={(ev) => tip.show(ev, [
                  `Over ${r.over}`,
                  `${r.runs} runs${r.wickets ? ` · ${r.wickets} wkt${r.wickets > 1 ? "s" : ""}` : ""}`,
                  `${r.fours} × 4 · ${r.sixes} × 6 · ${r.extras} extras`,
                ])}
                onMouseLeave={tip.hide} />
              {h > 0 && (
                <path d={`M${bx},${H - padB} V${top + rad} Q${bx},${top} ${bx + rad},${top} H${bx + w - rad} Q${bx + w},${top} ${bx + w},${top + rad} V${H - padB} Z`}
                  fill={inn.color} fillOpacity={r === best ? 1 : 0.82} pointerEvents="none" />
              )}
              {r === best && r.runs > 0 && (
                <text x={bx + w / 2} y={top - (r.wickets ? 13 : 3)} fontSize="7" textAnchor="middle" className="fill-text-primary font-semibold" pointerEvents="none">{r.runs}</text>
              )}
              {r.wickets > 0 && (
                <g pointerEvents="none">
                  <circle cx={bx + w / 2} cy={top - 5.5} r="4.6" fill="rgba(15,23,42,0.9)" />
                  {r.wickets > 1
                    ? <text x={bx + w / 2} y={top - 5.3} fontSize="6" textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="700">{r.wickets}</text>
                    : <path d={`M${bx + w / 2 - 2},${top - 7.5} l4,4 M${bx + w / 2 + 2},${top - 7.5} l-4,4`} stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />}
                </g>
              )}
              {(r.over === 1 || r.over % 5 === 0) && (
                <text x={bx + w / 2} y={H - 4} fontSize="7" textAnchor="middle" className="fill-text-secondary">{r.over}</text>
              )}
            </g>
          );
        })}
        <line x1={padL} x2={W - padR} y1={y(inn.rr)} y2={y(inn.rr)} stroke="var(--qc-axis)" strokeWidth="1" strokeDasharray="4 3" pointerEvents="none" />
        <text x={padL + 2} y={y(inn.rr) - 3} fontSize="6.5" className="fill-text-secondary" pointerEvents="none"
          style={{ paintOrder: "stroke", stroke: "var(--color-bg-secondary)", strokeWidth: 2.5, strokeLinejoin: "round" }}>RR {inn.rr.toFixed(1)}</text>
      </svg>
      {tip.node}
    </div>
  );
}

// ── 5. Worm ──────────────────────────────────────────────────────────
function Worm({ balls }) {
  const fmt = useContext(QcFormatContext);
  const innings = useInnings(balls);
  const [hover, setHover] = useState(null);
  if (!innings.length) return <Card title="Worm"><Empty>No balls in this selection.</Empty></Card>;
  const series = innings.map((inn) => {
    let cum = 0, wk = 0;
    const pts = [{ over: 0, runs: 0, wickets: 0, wk: 0 }];
    for (const r of inn.rows) {
      cum += r.runs;
      wk += r.wickets;
      pts.push({ over: r.over, runs: cum, wickets: r.wickets, wk });
    }
    return { ...inn, pts };
  });
  const maxOver = formatOvers(Math.max(1, ...series.flatMap((s) => s.pts.map((p) => p.over))), fmt);
  const maxRuns = Math.max(10, ...series.flatMap((s) => s.pts.map((p) => p.runs)));
  const W = 360, H = 200, padL = 28, padB = 18, padT = 14, padR = 34;
  const x = (o) => padL + (W - padL - padR) * (o / maxOver);
  const y = (v) => padT + (H - padT - padB) * (1 - v / maxRuns);
  const step = maxRuns > 250 ? 100 : maxRuns > 150 ? 50 : maxRuns > 60 ? 20 : 10;
  const yTicks = [];
  for (let t = 0; t <= maxRuns; t += step) yTicks.push(t);
  const xStep = maxOver > 20 ? 10 : 5;

  const onMove = (ev) => {
    const r = ev.currentTarget.getBoundingClientRect();
    const px = ((ev.clientX - r.left) / r.width) * W;
    setHover(Math.max(0, Math.min(maxOver, Math.round(((px - padL) / (W - padL - padR)) * maxOver))));
  };
  const dashed = (s) => series.filter((o) => o.teamId === s.teamId).indexOf(s) > 0;

  return (
    <Card title="Worm" subtitle="Cumulative runs by over · colour follows the batting side · ✕ = wicket">
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="Worm chart"
          onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.innings} id={`qc-worm-${s.innings}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity="0.22" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          <PhaseBands phases={phasesFor(maxOver, fmt)} x={x} y0={padT - 8} y1={H - padB} width={W - padR} />
          {yTicks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--qc-grid)" strokeWidth="0.6" />
              <text x={padL - 4} y={y(t)} fontSize="7" textAnchor="end" dominantBaseline="central" className="fill-text-secondary">{t}</text>
            </g>
          ))}
          {Array.from({ length: Math.floor(maxOver / xStep) + 1 }, (_, i) => i * xStep).map((o) => (
            <text key={o} x={x(o)} y={H - 5} fontSize="7" textAnchor="middle" className="fill-text-secondary">{o}</text>
          ))}
          {series.map((s) => !dashed(s) && (
            <path key={`a${s.innings}`}
              d={`M${x(0)},${y(0)} ${s.pts.map((p) => `L${x(p.over)},${y(p.runs)}`).join(" ")} L${x(s.pts[s.pts.length - 1].over)},${y(0)} Z`}
              fill={`url(#qc-worm-${s.innings})`} pointerEvents="none" />
          ))}
          {series.map((s) => {
            const last = s.pts[s.pts.length - 1];
            return (
              <g key={s.innings} pointerEvents="none">
                <polyline points={s.pts.map((p) => `${x(p.over)},${y(p.runs)}`).join(" ")}
                  fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                  strokeDasharray={dashed(s) ? "5 3" : undefined} />
                {s.pts.filter((p) => p.wickets > 0).map((p) => (
                  <g key={p.over}>
                    <circle cx={x(p.over)} cy={y(p.runs)} r="4" fill="rgba(15,23,42,0.9)" stroke={s.color} strokeWidth="1.2" />
                    <path d={`M${x(p.over) - 1.7},${y(p.runs) - 1.7} l3.4,3.4 M${x(p.over) + 1.7},${y(p.runs) - 1.7} l-3.4,3.4`} stroke="#fff" strokeWidth="1.1" strokeLinecap="round" />
                  </g>
                ))}
                <text x={x(last.over) + 5} y={y(last.runs)} fontSize="7.5" dominantBaseline="central" className="fill-text-primary font-semibold"
                  style={{ paintOrder: "stroke", stroke: "var(--color-bg-secondary)", strokeWidth: 3, strokeLinejoin: "round" }}>{`${last.runs}/${last.wk}`}</text>
              </g>
            );
          })}
          {hover != null && (
            <line x1={x(hover)} x2={x(hover)} y1={padT - 8} y2={H - padB} stroke="var(--qc-axis)" strokeWidth="0.8" />
          )}
        </svg>
        {hover != null && (
          <div className="pointer-events-none absolute top-1 left-10 rounded-md border border-border-default bg-bg-primary/95 px-2 py-1.5 text-[11px] shadow-lg">
            <div className="font-semibold text-text-primary">After over {hover}</div>
            {series.map((s) => {
              const p = [...s.pts].reverse().find((q) => q.over <= hover);
              return p ? (
                <div key={s.innings} className="text-text-secondary">
                  <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: s.color }} />
                  Inns {s.innings} {s.team}: <span className="text-text-primary tabular-nums">{p.runs}/{p.wk}</span>
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-text-secondary">
        {series.map((s) => (
          <span key={s.innings} className="inline-flex items-center gap-1.5">
            <svg width="18" height="6" aria-hidden><line x1="1" y1="3" x2="17" y2="3" stroke={s.color} strokeWidth="2" strokeDasharray={dashed(s) ? "4 2" : undefined} strokeLinecap="round" /></svg>
            Innings {s.innings} · {s.team} <span className="text-text-primary tabular-nums">{s.runs}/{s.wkts}</span>
          </span>
        ))}
      </div>
    </Card>
  );
}

// ── 6. Scoring mix (donut) ───────────────────────────────────────────
function arc(cx, cy, r0, r1, a0, a1) {
  const p = (a, r) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x1, y1] = p(a0, r1), [x2, y2] = p(a1, r1), [x3, y3] = p(a1, r0), [x4, y4] = p(a0, r0);
  return `M${x1},${y1} A${r1},${r1} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${r0},${r0} 0 ${large} 0 ${x4},${y4} Z`;
}

function ScoringMix({ balls }) {
  const tip = useTip();
  const rows = useMemo(() => {
    const def = [
      { key: "dot", label: "Dots", color: OC.dot, test: (b) => b.faced && b.offBat === 0 },
      { key: "1", label: "Singles", color: OC.r13, test: (b) => b.faced && b.offBat === 1 },
      { key: "23", label: "Twos & threes", color: OC.r23, test: (b) => b.faced && (b.offBat === 2 || b.offBat === 3) },
      { key: "4", label: "Fours", color: OC.r4, test: (b) => b.faced && (b.offBat === 4 || b.offBat === 5) },
      { key: "6", label: "Sixes", color: OC.r6, test: (b) => b.faced && b.offBat >= 6 },
      { key: "wd", label: "Wides", color: OC.extra, test: (b) => b.isWide },
    ];
    const out = def.map((d) => ({ ...d, balls: 0, runs: 0 }));
    for (const b of balls) {
      const r = out.find((d) => d.test(b));
      if (!r) continue;
      r.balls += 1;
      r.runs += r.key === "wd" ? b.total : b.offBat;
    }
    return out;
  }, [balls]);
  const totalBalls = rows.reduce((t, r) => t + r.balls, 0);
  const totalRuns = rows.reduce((t, r) => t + r.runs, 0);
  if (!totalBalls) return <Card title="Scoring mix"><Empty>No balls in this selection.</Empty></Card>;

  const live = rows.filter((r) => r.balls > 0);
  const gap = live.length > 1 ? 0.025 : 0;
  let a = -Math.PI / 2;
  const segs = live.map((r) => {
    const span = (r.balls / totalBalls) * Math.PI * 2;
    const s = { ...r, a0: a + gap / 2, a1: a + span - gap / 2 };
    a += span;
    return s;
  });
  const boundaryRuns = rows.filter((r) => r.key === "4" || r.key === "6").reduce((t, r) => t + r.runs, 0);

  return (
    <Card title="Scoring mix" subtitle={`${totalBalls} deliveries · ${totalRuns} runs (off the bat + wides)`}>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-center">
        <div data-tip-root className="relative sm:col-span-2 max-w-[210px] w-full mx-auto">
          <svg viewBox="0 0 100 100" className="w-full h-auto block" role="img" aria-label="Scoring mix">
            {segs.map((s) => (
              <path key={s.key} d={arc(50, 50, 30, 47, s.a0, Math.max(s.a0 + 0.001, s.a1))} fill={s.color}
                onMouseMove={(ev) => tip.show(ev, [s.label, `${s.balls} balls · ${pct(s.balls, totalBalls)}`, `${s.runs} runs`])}
                onMouseLeave={tip.hide} />
            ))}
            <text x="50" y="47" textAnchor="middle" fontSize="12" className="fill-text-primary" fontWeight="700" style={{ fontVariantNumeric: "tabular-nums" }}>{totalBalls}</text>
            <text x="50" y="58" textAnchor="middle" fontSize="5" className="fill-text-secondary" style={{ letterSpacing: 0.6 }}>BALLS</text>
          </svg>
          {tip.node}
        </div>
        <div className="sm:col-span-3">
          <table className="w-full text-[11px]">
            <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
              <tr><th className="text-left py-1 font-normal">Outcome</th><th className="text-right font-normal">Balls</th><th className="text-right font-normal">%</th><th className="text-right font-normal">Runs</th></tr>
            </thead>
            <tbody className="tabular-nums">
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-border-default">
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-2 text-text-primary">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: r.color }} />{r.label}
                    </span>
                  </td>
                  <td className="text-right text-text-primary font-semibold">{r.balls}</td>
                  <td className="text-right text-text-secondary">{pct(r.balls, totalBalls)}</td>
                  <td className="text-right text-text-secondary">{r.runs}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-[10px] text-text-secondary tabular-nums">
            {pct0(boundaryRuns, totalRuns)} of the runs came in boundaries
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── 7. Shot types ────────────────────────────────────────────────────
const SHOT_SEGS = [
  { key: "dot", color: OC.dot, label: "Dot", test: (b) => b.offBat === 0 },
  { key: "13", color: OC.r13, label: "1–3", test: (b) => b.offBat >= 1 && b.offBat <= 3 },
  { key: "4", color: OC.r4, label: "Four", test: (b) => b.offBat === 4 || b.offBat === 5 },
  { key: "6", color: OC.r6, label: "Six", test: (b) => b.offBat >= 6 },
];

function ShotTypes({ balls }) {
  const [sort, setSort] = useState("balls");
  const tip = useTip();
  const rows = useMemo(() => {
    const m = new Map();
    for (const b of balls) {
      if (!b.stroke || !b.faced) continue;
      const r = m.get(b.stroke) || { stroke: b.stroke, balls: 0, runs: 0, outs: 0, seg: { dot: 0, 13: 0, 4: 0, 6: 0 } };
      r.balls += 1;
      r.runs += b.offBat;
      if (b.wicket) r.outs += 1;
      const s = SHOT_SEGS.find((x) => x.test(b));
      if (s) r.seg[s.key] += 1;
      m.set(b.stroke, r);
    }
    const key = (r) => (sort === "sr" ? (r.balls ? r.runs / r.balls : 0) : r[sort]);
    return [...m.values()].sort((a, b) => key(b) - key(a) || b.balls - a.balls);
  }, [balls, sort]);
  if (!rows.length) return <Card title="Shot types"><Empty>No strokes coded in this selection.</Empty></Card>;
  const top = rows.slice(0, 12);
  const maxBalls = Math.max(1, ...top.map((r) => r.balls));

  return (
    <Card
      title="Shot types"
      subtitle={`${rows.length} strokes coded${rows.length > 12 ? " · top 12 shown" : ""} · bar = balls, split by outcome`}
      controls={<Toggle label="Sort shots" value={sort} onChange={setSort}
        options={[{ key: "balls", label: "Balls" }, { key: "runs", label: "Runs" }, { key: "sr", label: "SR" }]} />}
    >
      <div data-tip-root className="relative">
        <table className="w-full text-[11px]">
          <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left py-1 font-normal">Stroke</th><th className="w-[38%]" />
              <th className="text-right font-normal">Balls</th><th className="text-right font-normal">Runs</th>
              <th className="text-right font-normal">SR</th><th className="text-right font-normal">Out</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {top.map((r) => (
              <tr key={r.stroke} className="border-t border-border-default">
                <td className="py-1.5 text-text-primary whitespace-nowrap pr-2">{r.stroke}</td>
                <td className="pr-2">
                  <div className="flex h-2.5" style={{ width: `${(r.balls / maxBalls) * 100}%`, minWidth: 3, gap: 1 }}
                    onMouseMove={(ev) => tip.show(ev, [r.stroke, `${r.seg.dot} dots · ${r.seg[13]} × 1–3 · ${r.seg[4]} × 4 · ${r.seg[6]} × 6`, `${r.outs} out`])}
                    onMouseLeave={tip.hide}>
                    {SHOT_SEGS.filter((s) => r.seg[s.key]).map((s, i, arr) => (
                      <div key={s.key} style={{
                        flexGrow: r.seg[s.key], background: s.color,
                        borderTopRightRadius: i === arr.length - 1 ? 3 : 0, borderBottomRightRadius: i === arr.length - 1 ? 3 : 0,
                      }} />
                    ))}
                  </div>
                </td>
                <td className="text-right text-text-primary font-semibold">{r.balls}</td>
                <td className="text-right text-text-secondary">{r.runs}</td>
                <td className="text-right text-text-secondary">{r.balls ? ((r.runs * 100) / r.balls).toFixed(0) : "—"}</td>
                <td className="text-right text-text-primary">{r.outs || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {tip.node}
      </div>
      <div className="mt-2"><Legend items={SHOT_SEGS} /></div>
    </Card>
  );
}

const TAB_KEY = "zcd.qcChartsTab";
const readTab = () => {
  try { return localStorage.getItem(TAB_KEY) || "overview"; } catch { return "overview"; }
};

export default function QcCharts({ entries, filtered, onOpenBall, matchType = null }) {
  const balls = useMemo(() => entries.map(toBall), [entries]);
  const [tab, setTabState] = useState(readTab);
  const setTab = (t) => {
    setTabState(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* private mode */ }
  };
  const flagCount = useMemo(
    () => consistencyFlags(balls, { checkOvers: !filtered }).filter((f) => f.severity !== "check").length,
    [balls, filtered],
  );
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "match", label: "Match" },
    { key: "batting", label: "Batting" },
    { key: "bowling", label: "Bowling" },
    { key: "fielding", label: "Fielding & DRS" },
    { key: "checks", label: "Coding checks", badge: flagCount, badgeTone: flagCount ? "warn" : undefined },
  ];
  return (
    <QcFormatContext.Provider value={matchType}>
    <div className="qc-charts space-y-4">
      <style>{CHART_CSS}</style>
      <p className="text-[11px] text-text-secondary">
        {balls.length} balls{filtered ? " · charts follow the filters applied to the QC table" : " · whole match"}.
      </p>
      <SummaryTiles balls={balls} />
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "overview" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <WagonWheel balls={balls} />
          <PitchMap balls={balls} />
          <Beehive balls={balls} />
          <Manhattan balls={balls} />
          <Worm balls={balls} />
          <ScoringMix balls={balls} />
          <ShotTypes balls={balls} />
        </div>
      )}
      {tab === "match" && <MatchCharts balls={balls} />}
      {tab === "batting" && <BattingCharts balls={balls} />}
      {tab === "bowling" && <BowlingCharts balls={balls} />}
      {tab === "fielding" && <FieldingCharts balls={balls} />}
      {tab === "checks" && <CodingChecks balls={balls} filtered={filtered} onOpenBall={onOpenBall} />}
    </div>
    </QcFormatContext.Provider>
  );
}
